import { ReactNode } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'

import { InsufficientPermissions } from '../app/Auth/InsufficientPermissions/InsufficientPermissions'
import { BadgeEarnedPopup } from '../app/Gamification/BadgeEarnedPopup/BadgeEarnedPopup'
import { DonationWalletGiftPopup } from '../app/Onboarding/NewOnboarding/DonationWalletGiftPopup/DonationWalletGiftPopup'
import { NavBar } from '../common/components'
import { FullPageSpinner } from '../common/components/FullPageSpinner/FullPageSpinner'
import { withWorkplaceGivingAccess } from '../common/hoc/withPermissions'
import { useShouldRunOnboarding } from '../common/hooks/useShouldRunOnboarding/useShouldRunOnboarding'

import { Layout } from '@percent/workplace-giving/common/components/Layout/Layout'
import { useAuth } from '@percent/workplace-giving/common/hooks/useAuth/useAuth'
import { AppRoute } from '@percent/workplace-giving/routing/AppRoute.enum'
import { getLoginType } from '@percent/workplace-giving/context/auth/authContextController/AuthContextController'

function OutletWithNavBar({ withoutLayout }: Readonly<RouteProps>) {
  return (
    <>
      {withoutLayout ? null : <NavBar />}
      {withoutLayout ? null : <BadgeEarnedPopup />}
      {!withoutLayout && <DonationWalletGiftPopup />}
      <Outlet />
    </>
  )
}
const WGProtectedOutlet = withWorkplaceGivingAccess(OutletWithNavBar, InsufficientPermissions)

interface RouteProps {
  withoutLayout?: boolean
  children?: ReactNode
}

export function AllowOnboardedAccountOnlyRoute({ withoutLayout, children }: Readonly<RouteProps>) {
  const { state } = useAuth()
  const location = useLocation()
  const shouldRunOnboarding = useShouldRunOnboarding()

  if (state.status === 'loading' || state.status === 'first_load') {
    return <FullPageSpinner />
  }

  if (state.status === 'authorized' && shouldRunOnboarding) {
    return <Navigate to={AppRoute.NEW_ONBOARDING} replace state={{ from: location }} />
  }

  if (state.status === 'authorized') {
    if (withoutLayout) return <WGProtectedOutlet withoutLayout={withoutLayout} />

    return (
      <Layout>
        <WGProtectedOutlet withoutLayout={withoutLayout} />
        {children}
      </Layout>
    )
  }

  const signInRoute = getLoginType() === 'ep' ? AppRoute.SIGN_IN : AppRoute.SSO_SIGN_IN

  return <Navigate to={signInRoute} replace state={{ from: location }} />
}
